import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { NavLink, useLocation } from "react-router-dom";
import { Tooltip } from "antd";
import { ClearOutlined } from "@ant-design/icons";
import SmartTable from "../../../components/SmartTable";
import { useLoggedUser } from "../../../utils/hooks/useLoggedUser";
import PageMetaTagTitle from "../../../components/PageMetaTagTitle";
import { useTableFullHeight } from "../../../components/SmartTable/useTableFullHeight";
import { openModalAction } from "../../../components/Modal/reducer";
import Button from "../../../components/Button";
import { useDateFormat } from "../../../utils/hooks/useDateFormat";
import LoadingModal, { MODAL_EXPORT_LOADING } from "../../../components/SmartTable/ExportDropdown/LoadingModal";
import { PAGE_TRG_MASS_ASSIGNMENT } from "../routes";
import { usePermission } from "../../../components/_auth/permissions/usePermission";
import { PERMISSION_MANAGE_TRG } from "../../../components/_auth/permissions/permissions";
import { generateColumns, prepareQualificationsData, TABLE_QUALIFICATIONS } from "./methods";
import useQualificationsPageState from "./useQualificationsPageState";
import useQualificationsState from "./useQualificationsState";
import QualificationsFilters from "./QualificationsFilters";

/** Modal name constant used to open the add/edit qualification modal. */
export const MODAL_QUALIFICATION = "MODAL_QUALIFICATION";

/**
 * Features:
 * - External filter bar with contract type, qualification status, branch,
 *   department, area, job title, employee status, and expiry date range.
 * - "Mass Assignment" shortcut for users with TRG management permission.
 * - "Clear Filters" button that also purges all qualification-related localStorage keys.
 * - Table state (filters, page, search) is persisted to localStorage so returning
 *   from the user-detail page re-applies the previous context.
 * - Data transformation via `prepareQualificationsData` runs client-side after the
 *   API response to compute expiry flags, missing trainings, and rowSpan values.
 *
 * @returns {JSX.Element}
 */
const Qualifications = () => {
  const location = useLocation();
  const locationStatus = location.state?.status;
  const { format } = useDateFormat();
  const dispatch = useDispatch();
  const tableHeight = useTableFullHeight();
  const loggedUser = useLoggedUser();
  const { t } = useTranslation();
  const tableKeyName = `${TABLE_QUALIFICATIONS}_${loggedUser.id}`;
  const { localStorageTableFilters, currentPage, currentSearchValue } = useQualificationsPageState(
    tableKeyName,
    locationStatus
  );

  const permission = usePermission();
  const canManage = permission.canDo(PERMISSION_MANAGE_TRG);

  const {
    totalItems,
    setTotalItems,
    shouldReset,
    dateRange,
    branchesOptions,
    contractFilter,
    statusFilter,
    employeeStatusFilter,
    branchFilter,
    departmentFilter,
    areaFilter,
    jobTitleFilter,
    dateFilter,
    tableService,
    employeeStatusState,
    jobTitleState,
    departmentsState,
    areasState,
    apiDepartmentsOptions,
    apiAreasOptions,
    apiJobTitleOptions,
    apiEmployeeStatusOptions,
    customHandleTableChange,
    handleClearFilters,
    handleFilterChange,
    prepopulateFilter,
    handleDateChange
  } = useQualificationsState({ localStorageTableFilters, currentSearchValue, currentPage });

  const [totalPages, setTotalPages] = useState(1);

  return (
    <>
      <PageMetaTagTitle title="TRG qualifications" />
      <LoadingModal name={MODAL_EXPORT_LOADING} message={t("qam.loading_pdf_report_message")} />
      <SmartTable
        className="table-vertical-top"
        containerClassName="qualifications-table"
        externalActions={
          <>
            {canManage && (
              <NavLink to={PAGE_TRG_MASS_ASSIGNMENT} className="action-btn">
                <Button type="primary" icon={<i className="ti-check-box" />}>
                  {t("trg.qualifications.mass_assignment")}
                </Button>
              </NavLink>
            )}
          </>
        }
        clearFiltersActions={
          <Tooltip placement="topLeft" title={t("container.general_words.clear_filters")}>
            <Button onClick={handleClearFilters} icon={<ClearOutlined />}>
              {t("container.general_words.clear_filters")}
            </Button>
          </Tooltip>
        }
        externalFilters={
          <QualificationsFilters
            shouldReset={shouldReset}
            contractFilter={contractFilter}
            statusFilter={statusFilter}
            branchFilter={branchFilter}
            departmentFilter={departmentFilter}
            areaFilter={areaFilter}
            jobTitleFilter={jobTitleFilter}
            employeeStatusFilter={employeeStatusFilter}
            dateFilter={dateFilter}
            dateRange={dateRange}
            branchesOptions={branchesOptions}
            departmentsState={departmentsState}
            areasState={areasState}
            jobTitleState={jobTitleState}
            employeeStatusState={employeeStatusState}
            apiDepartmentsOptions={apiDepartmentsOptions}
            apiAreasOptions={apiAreasOptions}
            apiJobTitleOptions={apiJobTitleOptions}
            apiEmployeeStatusOptions={apiEmployeeStatusOptions}
            localStorageTableFilters={localStorageTableFilters}
            onFilterChange={handleFilterChange}
            onDateChange={handleDateChange}
            t={t}
            prepopulateFilter={prepopulateFilter}
          />
        }
        onGetData={(_, pager) => setTotalPages(pager?.pagination?.size || 1)}
        customHandleTableChange={customHandleTableChange}
        pagination={{
          hideOnSinglePage: true,
          showSizeChanger: false,
          defaultPageSize: tableService.getPerPage(),
          pageSize: totalItems || tableService.getPerPage(),
          total: (totalItems || tableService.getPerPage()) * totalPages
        }}
        rowSelection={false}
        scroll={{ x: 800, y: tableHeight - 47 }}
        name={TABLE_QUALIFICATIONS}
        tableService={tableService}
        columns={generateColumns(canManage, t, dispatch)}
        route="trg/qualifications"
        tableLocalStorageKeyName={tableKeyName}
        transformer={(data) => {
          const preparedData = prepareQualificationsData(data, t, format, tableService.getFilters());
          setTotalItems(preparedData.length);
          return preparedData;
        }}
        newItemProps={{
          access: canManage,
          text: t("trg.qualifications.add_qualification"),
          onClick: () => dispatch(openModalAction({ name: MODAL_QUALIFICATION }))
        }}
      />
    </>
  );
};

export default Qualifications;
